/**
 * What needs attention on the day, first: the Main board opens its left
 * column with it, above the trips. The day's hard findings as a short list,
 * each linking to its trip; Findings, under the map, stays the full account.
 *
 * Only hard findings, because a list that also holds every warning is no
 * longer short. "The day's" is what the panel calls the day's: a finding on
 * one of the day's trips or dated on the day. One about neither (a farm, a
 * driver across days, the run) is counted here and listed in the panel's
 * "About the whole plan". The data is the panel's own query, and each item is
 * the panel's `FindingItem`, so the two never disagree and nothing is asked
 * twice.
 */
import { EmptyState, Section } from "@depicomnlbv/platform-ui";
import type { UseQueryResult } from "@tanstack/react-query";

import type { Day, Finding, Findings } from "../api";
import { tripsInOrder, tripTitle } from "../trips";
import { FindingItem } from "./FindingsPanel";

export function NeedsAttention({ day, query }: { day: Day; query: UseQueryResult<Findings> }) {
  if (query.isPending) {
    // Plain text, not a second live region: Findings already announces that
    // the findings are being read.
    return (
      <Section title="Needs attention">
        <EmptyState>Reading the findings</EmptyState>
      </Section>
    );
  }
  if (query.isError) {
    // Not the block's error: Findings already reads the refusal out as an
    // alert, and a second alert would say the same thing twice.
    return (
      <Section title="Needs attention">
        <EmptyState>The findings could not be read; Findings says why.</EmptyState>
      </Section>
    );
  }
  const order = new Map(tripsInOrder(day.trips).map((t, i) => [t.route_id, i]));
  const onTrip = (f: Finding) => f.route_id !== null && order.has(f.route_id);
  const rank = (f: Finding) => (onTrip(f) ? order.get(f.route_id!)! : order.size);
  const hard = query.data.findings.filter((f) => f.severity === "hard");
  // Stable: findings on the same trip keep the API's order.
  const onDay = hard.filter((f) => onTrip(f) || f.date === day.date).sort((a, b) => rank(a) - rank(b));
  const onPlan = hard.filter((f) => f.date === null && f.route_id === null).length;

  return (
    <Section title="Needs attention">
      {onDay.length === 0 ? (
        <EmptyState>Nothing hard on this day.</EmptyState>
      ) : (
        <ul aria-label="Hard findings on this day" className="findings" role="list">
          {onDay.map((finding, index) => (
            <FindingItem
              finding={finding}
              key={`${finding.rule}-${index}`}
              source={false}
              tripName={tripTitle(day.trips, finding.route_id)}
            />
          ))}
        </ul>
      )}
      {onPlan > 0 ? (
        <p className="attention__more">
          {onPlan === 1
            ? "1 hard finding about the whole plan is listed under Findings."
            : `${onPlan} hard findings about the whole plan are listed under Findings.`}
        </p>
      ) : null}
    </Section>
  );
}
